import { useMemo, useState, useEffect } from "react";
import { useData } from "@/contexts/DataContext";
import { useLocation } from "wouter";
import { CheckCircle2, ChevronRight, X } from "lucide-react";

const DISMISS_KEY = "profile_banner_dismissed_session";

export function ProfileCompletionBanner() {
  const { businessProfile } = useData();
  const [, setLocation] = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  const { filled, total, missing } = useMemo(() => {
    const checks: { key: string; label: string; ok: boolean }[] = [
      { key: "email", label: "Email", ok: !!businessProfile?.businessEmail },
      { key: "phone", label: "Phone", ok: !!(businessProfile?.businessPhone && businessProfile.businessPhone !== "+91") },
      { key: "address", label: "Address", ok: !!businessProfile?.businessAddress },
      { key: "logo", label: "Logo", ok: !!businessProfile?.businessLogo },
    ];
    const filled = checks.filter((c) => c.ok).length;
    return {
      filled,
      total: checks.length,
      missing: checks.filter((c) => !c.ok).map((c) => c.label),
    };
  }, [businessProfile]);

  if (dismissed) return null;
  if (!businessProfile?.businessName) return null;
  if (filled === total) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="mx-3 mb-1 rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 flex items-start gap-3 relative"
      style={{ marginTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
        <CheckCircle2 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="flex-1 min-w-0 pr-5">
        <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
          Complete your profile ({filled}/{total})
        </p>
        <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-0.5">
          Add {missing.slice(0, 2).join(", ")}
          {missing.length > 2 ? ` +${missing.length - 2} more` : ""} — unlocks PDF & WhatsApp share
        </p>
        <button
          onClick={() => setLocation("/profile")}
          className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-700 dark:text-amber-300 active:scale-95"
        >
          Complete now <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 text-amber-700/60 hover:text-amber-700 dark:text-amber-300/60"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
